/**
 * @fileoverview PreviewStep Component - Blur Preview with Watermarks
 *
 * This component handles the preview step (Step 2) of the LetsBlur workflow.
 * It renders a canvas-based preview of the blurred image with watermarks
 * to protect against unauthorized use before download.
 *
 * @description
 * Key features:
 * - Canvas-based image rendering with blur effects
 * - Watermark overlay to protect preview images
 * - Statistics display (detected, blurred, total files)
 * - Multi-file navigation support
 *
 * @requires React
 * @requires ../utils/constants - Blur level configurations
 * @requires ../utils/blurUtils - Pixel blur algorithm
 */
import React, { useRef, useEffect } from 'react';
import { BLUR_LEVELS } from '../utils/constants';
import { applyPixelBlur } from '../utils/blurUtils';
import FileThumbnails from './FileThumbnails';

/**
 * PreviewStep Component
 *
 * @param {Object} props
 * @param {Array} props.files - Array of file objects with detection and blur data
 * @param {number} props.currentFileIndex - Index of the currently displayed file
 * @param {Function} props.onFileIndexChange - Callback to change the displayed file
 * @returns {JSX.Element|null}
 */
function PreviewStep({ files, currentFileIndex, onFileIndexChange }) {
  /* ========================================================================
     Refs and Derived State
     ======================================================================== */

  const canvasRef = useRef(null);
  const currentFile = files[currentFileIndex];

  /* ========================================================================
     Effects - Canvas Rendering
     ======================================================================== */

  /**
   * Effect: Render the blurred image on canvas when file or settings change
   * Applies pixel blur effect to configured face/body regions
   */
  useEffect(() => {
    if (!currentFile || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      // Apply blur for detected persons
      Object.entries(currentFile.blurSettings || {}).forEach(([id, settings]) => {
        if (!settings?.enabled) return;

        const person = currentFile.detections.find(d => d.id === id);
        if (!person) return;

        const pixelSize = BLUR_LEVELS[settings.level]?.pixelSize || 22;
        const target = settings.target || 'face';

        if ((target === 'face' || target === 'both') && person.faceBox) {
          applyPixelBlur(
            ctx,
            (person.faceBox.x / 100) * img.width,
            (person.faceBox.y / 100) * img.height,
            (person.faceBox.w / 100) * img.width,
            (person.faceBox.h / 100) * img.height,
            pixelSize
          );
        }

        if ((target === 'body' || target === 'both') && person.bodyBox) {
          applyPixelBlur(
            ctx,
            (person.bodyBox.x / 100) * img.width,
            (person.bodyBox.y / 100) * img.height,
            (person.bodyBox.w / 100) * img.width,
            (person.bodyBox.h / 100) * img.height,
            pixelSize
          );
        }
      });

      // Apply blur for custom blur boxes
      (currentFile.customBlurBoxes || []).forEach((customBox) => {
        if (!customBox?.enabled) return;

        const pixelSize = BLUR_LEVELS[customBox.level]?.pixelSize || 22;

        applyPixelBlur(
          ctx,
          (customBox.box.x / 100) * img.width,
          (customBox.box.y / 100) * img.height,
          (customBox.box.w / 100) * img.width,
          (customBox.box.h / 100) * img.height,
          pixelSize
        );
      });
    };

    img.src = currentFile.preview;
  }, [currentFile, currentFileIndex]);

  /* ========================================================================
     Watermark Generation
     ======================================================================== */

  /**
   * Generate watermark positions for grid overlay
   * Creates a diagonal pattern of watermarks across the image
   */
  const watermarks = [];
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 6; c++) {
      watermarks.push({ top: r * 12 - 5, left: c * 20 - 10 + (r % 2) * 10 });
    }
  }

  /* ========================================================================
     Render
     ======================================================================== */

  if (!currentFile) return null;

  return (
    <div className="max-w-4xl mx-auto w-full overflow-hidden">
      {/* ================================================================
          Header Section
          ================================================================ */}
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold">Preview Result</h2>
        <p className="text-gray-400 text-sm">Review before downloading</p>
      </div>

      {/* ================================================================
          File Navigation Tabs
          - Shown only when multiple files uploaded
          ================================================================ */}
      {files.length > 1 && (
        <FileThumbnails
          files={files}
          currentIndex={currentFileIndex}
          onIndexChange={onFileIndexChange}
        />
      )}

      {/* ================================================================
          Preview Content Card
          ================================================================ */}
      <div className="bg-white/5 backdrop-blur rounded-2xl p-3 sm:p-4 overflow-hidden">
        {/* Watermark Notice */}
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4 text-sm text-amber-200 text-center">
          ⚠️ Preview has watermarks. Download for clean version.
        </div>

        {/* Canvas Preview with Watermark Overlay */}
        <div className="relative rounded-lg overflow-hidden bg-black/50">
          <canvas ref={canvasRef} className="w-full max-w-full" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {watermarks.map((w, i) => (
              <span
                key={i}
                className="absolute text-sm font-bold text-white/15 whitespace-nowrap select-none"
                style={{
                  top: `${w.top}%`,
                  left: `${w.left}%`,
                  transform: 'rotate(-35deg)'
                }}
              >
                LETSBLUR.AI PREVIEW
              </span>
            ))}
          </div>
        </div>

        {/* Statistics Grid - responsive for mobile */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mt-4 text-center text-sm">
          <div className="p-2 sm:p-3 bg-white/5 rounded-lg min-w-0">
            <div className="text-lg sm:text-xl font-bold text-emerald-400">{currentFile.detections?.length || 0}</div>
            <div className="text-gray-400 text-xs sm:text-sm">Detected</div>
          </div>
          <div className="p-2 sm:p-3 bg-white/5 rounded-lg min-w-0">
            <div className="text-lg sm:text-xl font-bold text-purple-400">
              {(currentFile.customBlurBoxes || []).filter(b => b?.enabled).length}
            </div>
            <div className="text-gray-400 text-xs sm:text-sm">BlurBox</div>
          </div>
          <div className="p-2 sm:p-3 bg-white/5 rounded-lg min-w-0">
            {(() => {
              const detectedBlurred = Object.values(currentFile.blurSettings || {}).filter(s => s?.enabled).length;
              const customBoxCount = (currentFile.customBlurBoxes || []).filter(b => b?.enabled).length;
              return (
                <>
                  <div className="text-lg sm:text-xl font-bold">
                    <span className="text-green-400">{detectedBlurred}</span>
                    <span className="text-gray-400">+</span>
                    <span className="text-purple-400">{customBoxCount}</span>
                  </div>
                  <div className="text-gray-400 text-xs sm:text-sm">Blurred</div>
                </>
              );
            })()}
          </div>
          <div className="p-2 sm:p-3 bg-white/5 rounded-lg min-w-0">
            <div className="text-lg sm:text-xl font-bold text-blue-400">{files.length}</div>
            <div className="text-gray-400 text-xs sm:text-sm">Files</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreviewStep;
